import { Cpu, BellRing, Zap } from "lucide-react";
import { api } from "@/lib/api";
import { useLive } from "@/context/LiveContext";
import { StatusBadge } from "./StatusBadge";

const fmt = (d) => (d ? new Date(d).toLocaleString("es-UY", { timeZone: "America/Montevideo", hour12: false, dateStyle: "short", timeStyle: "short" }) : "nunca");

export function AgentCard() {
  const { agents, connected } = useLive();
  const a = agents.find((x) => x.online) || agents.find((x) => !x.revoked);
  const caps = a ? Object.entries(a.capabilities || {}).filter(([, v]) => v?.available).map(([k]) => k) : [];
  return (
    <section className="hud-panel p-3" data-testid="agent-card">
      <div className="flex items-center justify-between"><span className="hud-label flex items-center gap-1"><Cpu className="h-3 w-3" />Agente Windows</span>
        <StatusBadge status={!connected ? "desconocido" : a ? (a.online ? "conectado" : "desconectado") : "pendiente"} testId="agent-status-badge" /></div>
      <p className="mt-2 text-xs text-slate-300" data-testid="agent-name">{a ? a.name : "Sin emparejar"}</p>
      <p className="text-[10px] text-slate-500" data-testid="agent-last-seen">Último contacto: {fmt(a?.last_seen)}</p>
      {a?.capabilities?.simulated && <p className="text-[10px] text-amber-300 uppercase tracking-widest" data-testid="agent-simulated">Agente simulado (pruebas)</p>}
      {caps.length > 0 && <p className="mt-1 text-[10px] text-cyan-400/70">{caps.join(" · ")}</p>}
      <p className="mt-1 text-[10px] text-slate-600">Canal en vivo: {connected ? "activo" : "reconectando…"}</p>
    </section>
  );
}

export function CurrentAction() {
  const { commands, routineRun } = useLive();
  const active = commands.find((c) => ["pendiente", "reclamada", "ejecutando"].includes(c.status));
  const last = active || commands[0];
  return (
    <section className="hud-panel p-3" data-testid="current-action">
      <span className="hud-label flex items-center gap-1"><Zap className="h-3 w-3" />Acción actual</span>
      {last ? (
        <div className="mt-2 text-xs">
          <div className="flex items-center justify-between gap-2"><span className="text-cyan-200 truncate" data-testid="current-action-tool">{last.tool}</span><StatusBadge status={last.status} testId="current-action-status" /></div>
          {last.awaiting_confirmation && <p className="text-[10px] text-amber-300">Esperando su autorización</p>}
          {last.result?.error && <p className="text-[10px] text-red-300">{last.result.error}</p>}
          {last.result?.simulated && <p className="text-[10px] text-amber-300">SIMULADO</p>}
          {!active && <p className="text-[10px] text-slate-500">Última orden · sin acciones en curso</p>}
        </div>
      ) : <p className="mt-2 text-xs text-slate-500">Sin acciones registradas</p>}
      {routineRun && (
        <div className="mt-2 border-t border-cyan-500/20 pt-2 text-[11px]" data-testid="current-routine">
          <div className="flex justify-between"><span className="text-cyan-200">Rutina: {routineRun.routine?.name}</span><span className="text-slate-400">{routineRun.status}</span></div>
          {routineRun.steps?.map((s) => <div key={s.step_id} className="flex justify-between text-slate-400"><span className="truncate">{s.title}</span><span>{s.status}</span></div>)}
        </div>
      )}
    </section>
  );
}

export function Reminders() {
  const { tasks, refreshTasks } = useLive();
  const next = tasks.filter((t) => t.status === "activo").slice(0, 5);
  return (
    <section className="hud-panel p-3" data-testid="reminders-panel">
      <span className="hud-label flex items-center gap-1"><BellRing className="h-3 w-3" />Próximos recordatorios</span>
      <ul className="mt-2 space-y-2">
        {next.length === 0 && <li className="text-xs text-slate-500">Nada programado</li>}
        {next.map((t) => (
          <li key={t.id} className="text-xs" data-testid={`reminder-${t.id}`}>
            <div className="text-slate-200">{t.title}</div>
            <div className="text-[10px] text-cyan-400/70">{t.next_run_human}</div>
          </li>
        ))}
      </ul>
      {next.length > 0 && <button className="mt-2 text-[10px] text-slate-500 hover:text-cyan-300" data-testid="reminders-refresh" onClick={refreshTasks}>Actualizar</button>}
    </section>
  );
}

export async function stopEverything() {
  return api.post("/commands/stop-all").then((r) => r.data);
}
